import React, { useCallback, useEffect, useState } from 'react';
import {
  LiveEvent, EventOccurrence, EventApplication, EventParticipant,
  getEvents, getEventOccurrences,
  getEventApplicationsFull, getOccurrenceParticipantsFull,
  addOccurrenceParticipants, updateEventApplication,
  updateOccurrenceParticipant, advanceWinners,
  removeOccurrenceParticipant, deleteEventApplication, setSessionLock,
  addParticipantPerformance, updateParticipantPerformance, deleteParticipantPerformance,
  EventLaunchStatus, getEventLaunchStatus, launchEvent,
  EventRequest, getEventRequests, createEventRequest,
} from './api';

interface Props {
  streamerId: number;
  isMobile?: boolean;
}

const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];

const STATUS_COLOR: Record<string, string> = {
  upcoming: '#6c757d',
  active: '#28a745',
  completed: '#17a2b8',
  cancelled: '#dc3545',
};
const STATUS_LABEL: Record<string, string> = {
  upcoming: 'À venir',
  active: 'En cours',
  completed: 'Terminé',
  cancelled: 'Annulé',
};
const PART_STATUS_LABEL: Record<string, string> = {
  competing: 'En lice',
  eliminated: 'Éliminé',
  finalist: 'Finaliste',
  winner: 'Gagnant',
};
const PART_STATUS_COLOR: Record<string, string> = {
  competing: '#6c757d',
  eliminated: '#dc3545',
  finalist: '#fd7e14',
  winner: '#28a745',
};

function formatDate(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getDate()} ${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;
}

function formatTime(t?: string | null) {
  return t ? t.slice(0, 5) : '';
}

function fmtDateTime(s?: string | null) {
  if (!s) return '';
  const iso = s.includes('T') ? s : s.replace(' ', 'T');
  const d = new Date(iso);
  if (isNaN(d.getTime())) return s;
  return `${d.getDate()} ${MONTHS_FR[d.getMonth()]} ${d.getFullYear()} à ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function tkImg(url?: string | null) {
  if (!url) return '';
  return /tiktokcdn|ibyteimg|muscdn|bytecdn/i.test(url) ? `/api/gift-image?url=${encodeURIComponent(url)}` : url;
}

function tiktokUrl(handle?: string | null) {
  const h = (handle || '').trim().replace(/^@/, '');
  return h ? `https://www.tiktok.com/@${encodeURIComponent(h)}/live` : '#';
}

function Avatar({ url, name }: { url?: string | null; name?: string | null }) {
  const src = tkImg(url);
  return src
    ? <img src={src} alt="" onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: '#e2e8f0' }} />
    : <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{(name || '?').charAt(0).toUpperCase()}</div>;
}

function isImageUrl(u?: string | null) {
  return !!u && /\.(jpe?g|png|gif|webp|bmp)(\?|$|&)/i.test(u);
}
function isVideoUrl(u?: string | null) {
  return !!u && /\.(mp4|mov|webm|m4v|ogg)(\?|$|&)/i.test(u);
}
function isAudioUrl(u?: string | null) {
  return !!u && /\.(mp3|wav|m4a|aac|oga|opus|flac)(\?|$|&)/i.test(u);
}
// Dropbox : convertir un lien de partage en lien direct (aperçu inline)
function directUrl(u?: string | null) {
  if (!u) return '';
  if (u.indexOf('dropbox.com') !== -1) {
    let r = u.replace('?dl=0', '').replace('?dl=1', '').replace('&dl=0', '').replace('&dl=1', '');
    r += (r.indexOf('?') !== -1 ? '&' : '?') + 'raw=1';
    return r;
  }
  return u;
}

function fileAccept(field_options?: string | null): string {
  try { const o = JSON.parse(field_options || '{}'); return o.accept || 'any'; }
  catch { return 'any'; }
}

function AnswersPanel({ answers }: { answers?: Array<{ id: number; label?: string; field_type?: string; field_options?: string | null; answer_text?: string | null; file_url?: string | null }> }) {
  if (!answers || answers.length === 0)
    return <div style={{ color: '#94a3b8', fontSize: 12, padding: '8px 0' }}>Aucune réponse.</div>;
  const isDropbox = (u?: string | null) => !!u && u.indexOf('dropbox.com') !== -1;
  const asUrl = (s?: string | null) => (s && /^https?:\/\/\S+$/i.test(s.trim())) ? s.trim() : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '10px 0 2px', minWidth: 0 }}>
      {answers.map(a => {
        // L'URL du fichier peut être dans file_url OU dans answer_text (form public)
        const fileUrl = a.file_url || asUrl(a.answer_text);
        const raw = directUrl(fileUrl);
        const acc = fileAccept(a.field_options);
        // Type média : extension de l'URL sinon type attendu du champ
        const kind = isImageUrl(fileUrl) || acc === 'image' ? 'image'
          : isVideoUrl(fileUrl) || acc === 'video' ? 'video'
          : isAudioUrl(fileUrl) || acc === 'audio' ? 'audio'
          : 'other';
        return (
          <div key={a.id} style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3, fontWeight: 600 }}>{a.label || 'Champ'}</div>
            {fileUrl ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                {kind === 'image' && (
                  <a href={raw} target="_blank" rel="noreferrer"><img src={raw} alt="" style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 8, border: '1px solid #e2e8f0', display: 'block' }} /></a>
                )}
                {kind === 'video' && (
                  <video src={raw} controls playsInline style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 8, border: '1px solid #e2e8f0', background: '#000' }} />
                )}
                {kind === 'audio' && (
                  <audio src={raw} controls style={{ width: '100%', maxWidth: 360 }} />
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {isDropbox(fileUrl) && (
                    <a href={fileUrl!} target="_blank" rel="noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, background: '#0061fe', color: '#fff', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                      📦 Ouvrir sur Dropbox
                    </a>
                  )}
                  <a href={raw} target="_blank" rel="noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid #7c3aed', color: '#7c3aed', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                    📎 Ouvrir / télécharger
                  </a>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#334155', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{a.answer_text || <em style={{ color: '#94a3b8' }}>—</em>}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function jlist(v?: string | null): string[] {
  try { const a = JSON.parse(v || '[]'); return Array.isArray(a) ? a.filter(Boolean) : []; }
  catch { return []; }
}

function EventRecap({ event, sessionsCount, posterFilename }: {
  event: LiveEvent; sessionsCount: number; posterFilename?: string | null;
}) {
  const poster = posterFilename || event.image_filename;
  const rules = jlist(event.rules_json);
  const prizes = jlist(event.prizes_json);
  const streamers = event.streamers || [];
  return (
    <div style={{ display: 'flex', gap: 18, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16, flexWrap: 'wrap' }}>
      {poster && (
        <div style={{ flexShrink: 0, width: 150 }}>
          <img src={`/api/event-images/${poster}`} alt="" style={{ width: 150, borderRadius: 10, display: 'block', border: '1px solid #e2e8f0' }} />
          <a href={`/api/event-images/${poster}`} download={`affiche-${event.title || 'evenement'}`}
            style={{ display: 'block', textAlign: 'center', marginTop: 6, fontSize: 12, fontWeight: 600, color: '#7c3aed', textDecoration: 'none' }}>
            ⤓ Télécharger l'affiche
          </a>
        </div>
      )}
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{event.title}</div>
        {event.description && <div style={{ fontSize: 13, color: '#475569', marginTop: 4, whiteSpace: 'pre-wrap' }}>{event.description}</div>}
        {streamers.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Animé par</span>
            {streamers.map(s => (
              <span key={s.streamer_id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Avatar url={s.avatar_url} name={s.display_name} />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{s.display_name}</span>
              </span>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 10, fontSize: 13, color: '#334155' }}>
          {event.recurrence_summary && <div>🗓 <strong>{event.recurrence_summary}</strong></div>}
          <div>🎬 <strong>{sessionsCount}</strong> session{sessionsCount > 1 ? 's' : ''}</div>
        </div>
        {(rules.length > 0 || prizes.length > 0) && (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 12 }}>
            {rules.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>📋 Règles</div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#334155', lineHeight: 1.6 }}>
                  {rules.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
            {prizes.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>🏆 À gagner</div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#334155', lineHeight: 1.6 }}>
                  {prizes.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  padding: '14px 16px',
  marginBottom: 12,
  cursor: 'pointer',
  transition: 'border-color 0.15s',
};

const badge = (color: string): React.CSSProperties => ({
  display: 'inline-block',
  background: color + '22',
  color,
  border: `1px solid ${color}44`,
  borderRadius: 4,
  padding: '1px 7px',
  fontSize: 11,
  fontWeight: 600,
  marginLeft: 6,
});

const btn = (color = '#7c5cbf'): React.CSSProperties => ({
  background: color,
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '7px 14px',
  fontSize: 13,
  cursor: 'pointer',
  fontWeight: 600,
});

const btnOutline = (color = '#7c5cbf'): React.CSSProperties => ({
  background: 'transparent',
  color,
  border: `1px solid ${color}`,
  borderRadius: 6,
  padding: '6px 13px',
  fontSize: 13,
  cursor: 'pointer',
  fontWeight: 600,
});

const inputSt: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  padding: '6px 10px',
  color: '#0f172a',
  fontSize: 13,
  width: '100%',
  boxSizing: 'border-box',
};

// ─── OccurrenceCard ────────────────────────────────────────────────────────
interface OccCard {
  occ: EventOccurrence;
  event: LiveEvent;
  onClick: () => void;
}
function OccurrenceCard({ occ, event, onClick }: OccCard) {
  const color = STATUS_COLOR[occ.status] ?? '#888';
  return (
    <div style={{ ...card, borderColor: '#e2e8f0' }} onClick={onClick}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        {(occ.image_filename || event.image_filename) && (
          <img
            src={`/api/event-images/${occ.image_filename || event.image_filename}`}
            alt=""
            style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 4 }}>
            {event.title}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
            {formatDate(occ.occurrence_date)}
            {occ.time_start && ` · ${formatTime(occ.time_start)}`}
            {occ.time_end && ` – ${formatTime(occ.time_end)}`}
          </div>
          <div>
            <span style={badge(color)}>{STATUS_LABEL[occ.status] ?? occ.status}</span>
            {event.event_type === 'concours' && (
              <span style={badge('#7c3aed')}>Concours</span>
            )}
          </div>
          {occ.notes && (
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, fontStyle: 'italic' }}>
              {occ.notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ParticipantRow (session) ──────────────────────────────────────────────
interface PartRowProps {
  p: EventParticipant;
  eventId: number;
  occId: number;
  locked: boolean;
  canManage: boolean;
  isLast: boolean;
  onUpdated: () => void;
  onRemove: () => void;
  onDelete: () => void;
}
function ParticipantRow({ p, eventId, occId, locked, canManage, isLast, onUpdated, onRemove, onDelete }: PartRowProps) {
  const [status, setStatus] = useState(p.status);
  const [passOrder, setPassOrder] = useState<string>(p.pass_order != null ? String(p.pass_order) : '');
  const [open, setOpen] = useState(false);
  const [showPerf, setShowPerf] = useState(false);
  const [pfs, setPfs] = useState<Array<{ id?: number; score: string; notes: string }>>(
    (p.performances || []).map(x => ({ id: x.id, score: x.score != null ? String(x.score) : '', notes: x.notes ?? '' }))
  );
  const [busyP, setBusyP] = useState(false);

  useEffect(() => {
    setPfs((p.performances || []).map(x => ({ id: x.id, score: x.score != null ? String(x.score) : '', notes: x.notes ?? '' })));
  }, [p.performances]);

  const saveStatus = async (s: typeof status) => {
    setStatus(s);
    try { await updateOccurrenceParticipant(eventId, occId, p.id, { status: s }); onUpdated(); }
    catch (e) { console.error(e); }
  };
  const savePassOrder = async () => {
    try {
      await updateOccurrenceParticipant(eventId, occId, p.id, { pass_order: passOrder !== '' ? parseInt(passOrder, 10) : null });
      onUpdated();
    } catch (e) { console.error(e); }
  };
  const addPerf = async () => {
    setBusyP(true);
    try { await addParticipantPerformance(eventId, occId, p.id, { score: null, notes: '' }); onUpdated(); }
    catch (e) { console.error(e); } finally { setBusyP(false); }
  };
  const savePerf = async (i: number) => {
    const pf = pfs[i];
    setBusyP(true);
    try {
      const payload = { score: pf.score !== '' ? parseFloat(pf.score) : null, notes: pf.notes || null };
      if (pf.id) await updateParticipantPerformance(eventId, occId, p.id, pf.id, payload);
      else await addParticipantPerformance(eventId, occId, p.id, payload);
      onUpdated();
    } catch (e) { console.error(e); } finally { setBusyP(false); }
  };
  const delPerf = async (i: number) => {
    const pf = pfs[i];
    if (!pf.id) { setPfs(a => a.filter((_, j) => j !== i)); return; }
    if (!window.confirm('Supprimer cette prestation ?')) return;
    setBusyP(true);
    try { await deleteParticipantPerformance(eventId, occId, p.id, pf.id); onUpdated(); }
    catch (e) { console.error(e); } finally { setBusyP(false); }
  };

  const color = PART_STATUS_COLOR[status] ?? '#64748b';
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginBottom: 10, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {passOrder !== '' && (
          <span style={{ width: 26, height: 26, borderRadius: '50%', background: '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{passOrder}</span>
        )}
        <Avatar url={p.tk_avatar} name={p.tk_name || p.applicant_name || p.applicant_tiktok} />
        <div style={{ flex: 1, minWidth: 140 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', overflowWrap: 'anywhere' }}>
            {p.tk_name || p.applicant_name || (p.applicant_tiktok ? `@${p.applicant_tiktok}` : 'Candidat')}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', overflowWrap: 'anywhere' }}>
            {p.applicant_tiktok ? `@${p.applicant_tiktok}` : ''}
            {p.created_at ? ` · inscrit le ${fmtDateTime(p.created_at)}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {p.avg_score != null && (
            <span style={{ ...badge('#0f172a'), marginLeft: 0 }}>★ Moy. {p.avg_score}/10</span>
          )}
          {p.applicant_tiktok && (
            <a href={tiktokUrl(p.applicant_tiktok)} target="_blank" rel="noreferrer" title="Live / profil TikTok"
              style={{ ...btnOutline('#0f172a'), padding: '4px 9px', fontSize: 12, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
              🔴 TikTok
            </a>
          )}
          <select value={status} disabled={locked || !canManage}
            onChange={e => saveStatus(e.target.value as typeof status)}
            style={{ padding: '4px 8px', borderRadius: 6, border: `1px solid ${color}`, color, fontSize: 12, fontWeight: 600, background: color + '14', cursor: locked ? 'not-allowed' : 'pointer' }}>
            {(['competing', 'eliminated', 'finalist', 'winner'] as const).filter(s => !(isLast && s === 'finalist')).map(s => (
              <option key={s} value={s}>{PART_STATUS_LABEL[s]}</option>
            ))}
          </select>
          {canManage && !locked && (
            <input type="number" min={1} value={passOrder} placeholder="N°"
              title="Ordre de passage"
              onChange={e => setPassOrder(e.target.value)} onBlur={savePassOrder}
              style={{ width: 52, padding: '4px 6px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, textAlign: 'center' }} />
          )}
          <button onClick={() => setShowPerf(o => !o)} style={{ ...btnOutline('#7c3aed'), padding: '4px 9px', fontSize: 12 }}>
            {showPerf ? '▲ Prestation' : '▶ Prestation'}
          </button>
          <button onClick={() => setOpen(o => !o)} style={{ ...btnOutline('#64748b'), padding: '4px 9px', fontSize: 12 }}>
            {open ? '▲ Réponses' : '▼ Réponses'}
          </button>
        </div>
      </div>

      {open && <AnswersPanel answers={p.answers} />}

      {showPerf && (
        <div style={{ marginTop: 10, borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
          {pfs.length === 0 && (
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>Aucune prestation notée.</div>
          )}
          {pfs.map((pf, i) => (
            <div key={pf.id ?? `n${i}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap' }}>
              <div style={{ width: 90 }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Note /10</div>
                <input type="number" min={0} max={10} step={0.5} value={pf.score} disabled={locked}
                  onChange={e => setPfs(a => a.map((x, j) => j === i ? { ...x, score: e.target.value } : x))}
                  style={{ ...inputSt, width: 90 }} />
              </div>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Annotation {i + 1}</div>
                <textarea value={pf.notes} disabled={locked}
                  onChange={e => setPfs(a => a.map((x, j) => j === i ? { ...x, notes: e.target.value } : x))}
                  style={{ ...inputSt, minHeight: 38, resize: 'vertical' }} />
              </div>
              {!locked && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', paddingTop: 18 }}>
                  <button onClick={() => savePerf(i)} disabled={busyP} style={{ ...btn('#16a34a'), padding: '6px 10px', fontSize: 12 }}>💾</button>
                  <button onClick={() => delPerf(i)} disabled={busyP} style={{ ...btnOutline('#dc2626'), padding: '6px 10px', fontSize: 12 }}>🗑</button>
                </div>
              )}
            </div>
          ))}
          {!locked && (
            <button onClick={addPerf} disabled={busyP} style={{ ...btnOutline('#7c3aed'), padding: '5px 12px', fontSize: 12 }}>
              + Ajouter une prestation
            </button>
          )}
        </div>
      )}

      {locked ? (
        <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', marginTop: 10 }}>
          🔒 Session clôturée — décloture-la pour modifier ou déplacer ce candidat.
        </div>
      ) : canManage && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10, justifyContent: 'flex-end' }}>
          <button onClick={onRemove} style={{ ...btnOutline('#d97706'), padding: '5px 10px', fontSize: 11 }}>↩ Remettre en candidature</button>
          <button onClick={onDelete} style={{ ...btnOutline('#dc2626'), padding: '5px 10px', fontSize: 11 }}>🗑 Supprimer</button>
        </div>
      )}
    </div>
  );
}

// ─── EventDetail ───────────────────────────────────────────────────────────
interface EventDetailProps {
  event: LiveEvent;
  onBack: () => void;
  showBack?: boolean;
}
function EventDetail({ event, onBack, showBack }: EventDetailProps) {
  const isConcours = event.event_type === 'concours';
  const [occs, setOccs] = useState<EventOccurrence[]>([]);
  const [apps, setApps] = useState<EventApplication[]>([]);
  const [partMap, setPartMap] = useState<Record<number, EventParticipant[]>>({});
  const [tab, setTab] = useState<'cand' | number | 'sess1'>('cand');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [advancing, setAdvancing] = useState(false);
  const [launch, setLaunch] = useState<EventLaunchStatus | null>(null);
  const [launchDate, setLaunchDate] = useState('');
  const [launching, setLaunching] = useState(false);

  const sessions = [...occs]
    .filter(o => o.status !== 'cancelled')
    .sort((a, b) => (a.occurrence_date + (a.time_start || '')).localeCompare(b.occurrence_date + (b.time_start || '')));

  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      const or = await getEventOccurrences(event.id);
      setOccs(or.data);
      if (isConcours) {
        try { const ar = await getEventApplicationsFull(event.id); setApps(ar.data); } catch { setApps([]); }
        try {
          const ls = await getEventLaunchStatus(event.id);
          setLaunch(ls.data);
          if (ls.data.proposed_start_date) setLaunchDate(d => d || ls.data.proposed_start_date || '');
        } catch { setLaunch(null); }
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [event.id, isConcours]);

  useEffect(() => { loadBase(); }, [loadBase]);

  const loadParts = useCallback(async (occId: number) => {
    try {
      const r = await getOccurrenceParticipantsFull(event.id, occId);
      setPartMap(m => ({ ...m, [occId]: r.data }));
    } catch { setPartMap(m => ({ ...m, [occId]: [] })); }
  }, [event.id]);

  useEffect(() => {
    if (typeof tab === 'number' && partMap[tab] === undefined) loadParts(tab);
  }, [tab, partMap, loadParts]);

  const toggle = (id: number) => setExpanded(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const canManage = !!launch?.can_manage;

  const handleSelect = async (appId: number) => {
    setBusy(appId);
    try {
      await updateEventApplication(event.id, appId, { overall_status: 'active' });
      if (sessions[0]) await addOccurrenceParticipants(event.id, sessions[0].id, [appId]);
      await loadBase();
      if (sessions[0]) await loadParts(sessions[0].id);
    } catch (e) { console.error(e); } finally { setBusy(null); }
  };

  const handleUnselect = async (appId: number) => {
    setBusy(appId);
    try {
      await updateEventApplication(event.id, appId, { overall_status: 'pending' });
      await loadBase();
    } catch (e) { console.error(e); } finally { setBusy(null); }
  };

  const handleDeleteApp = async (appId: number) => {
    if (!window.confirm('Supprimer définitivement ce candidat (et ses réponses) ?')) return;
    setBusy(appId);
    try {
      await deleteEventApplication(event.id, appId);
      await loadBase();
    } catch (e) { console.error(e); } finally { setBusy(null); }
  };

  const handleRemovePart = async (occId: number, pid: number) => {
    if (!window.confirm('Retirer ce candidat de la session et le remettre en candidature ?')) return;
    try {
      await removeOccurrenceParticipant(event.id, occId, pid);
      await loadParts(occId);
      await loadBase();
    } catch (e) { console.error(e); }
  };

  const handleToggleLock = async (occId: number, currentlyLocked: boolean) => {
    try {
      await setSessionLock(event.id, occId, !currentlyLocked);
      await loadBase();
      await loadParts(occId);
    } catch (e) { console.error(e); }
  };

  const handleAdvance = async (occId: number) => {
    const idx = sessions.findIndex(o => o.id === occId);
    setAdvancing(true);
    try {
      await advanceWinners(event.id, occId);
      await loadParts(occId);
      const next = sessions[idx + 1];
      const last = sessions[sessions.length - 1];
      if (next) await loadParts(next.id);
      if (last && last.id !== next?.id) await loadParts(last.id);
    } catch (e) { console.error(e); } finally { setAdvancing(false); }
  };

  const handleLaunch = async () => {
    if (!launchDate) return;
    setLaunching(true);
    try {
      await launchEvent(event.id, launchDate);
      const or = await getEventOccurrences(event.id);
      setOccs(or.data);
      const first = [...or.data].filter(o => o.status !== 'cancelled')
        .sort((a, b) => a.occurrence_date.localeCompare(b.occurrence_date))[0];
      if (first) {
        const activeIds = apps.filter(a => a.overall_status === 'active').map(a => a.id);
        if (activeIds.length) await addOccurrenceParticipants(event.id, first.id, activeIds);
        await loadParts(first.id);
        setTab(first.id);
      }
      await loadBase();
    } catch (e) { console.error(e); } finally { setLaunching(false); }
  };

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 14px', fontSize: 13, fontWeight: active ? 700 : 500,
    color: active ? '#7c3aed' : '#64748b', border: 'none',
    borderBottom: active ? '2px solid #7c3aed' : '2px solid transparent',
    cursor: 'pointer', background: 'none', whiteSpace: 'nowrap',
  });

  const pendingApps = apps.filter(a => a.overall_status === 'pending');
  const selectedApps = apps.filter(a => a.overall_status === 'active');
  const selectedCount = launch?.selected_count ?? selectedApps.length;
  const minP = launch?.min_participants ?? (event.min_participants || 0);
  const conditionalPending = !!launch?.enabled && !launch?.launched && sessions.length === 0;

  const candRow = (a: EventApplication, action: React.ReactNode) => (
    <div key={a.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginBottom: 10, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Avatar url={a.tk_avatar} name={a.tk_name || a.applicant_name || a.applicant_tiktok} />
        <div style={{ flex: 1, minWidth: 140 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', overflowWrap: 'anywhere' }}>
            {a.tk_name || a.applicant_name || (a.applicant_tiktok ? `@${a.applicant_tiktok}` : 'Candidat')}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', overflowWrap: 'anywhere' }}>
            {a.applicant_tiktok ? `@${a.applicant_tiktok}` : ''}
            {a.created_at ? ` · inscrit le ${fmtDateTime(a.created_at)}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {a.applicant_tiktok && (
            <a href={tiktokUrl(a.applicant_tiktok)} target="_blank" rel="noreferrer"
              title="Voir le live / profil TikTok"
              style={{ ...btnOutline('#0f172a'), padding: '5px 10px', fontSize: 12, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
              🔴 TikTok
            </a>
          )}
          <button onClick={() => toggle(a.id)} style={{ ...btnOutline('#64748b'), padding: '5px 10px', fontSize: 12 }}>
            {expanded.has(a.id) ? '▲ Réponses' : '▼ Réponses'}
          </button>
          {action}
        </div>
      </div>
      {expanded.has(a.id) && <AnswersPanel answers={a.answers} />}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        {showBack && <button onClick={onBack} style={{ ...btnOutline('#7c3aed'), padding: '5px 10px', fontSize: 12 }}>← Retour</button>}
        <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{event.title}</div>
        {isConcours && <span style={badge('#7c3aed')}>Concours</span>}
      </div>
      {event.description && (
        <div style={{ color: '#475569', fontSize: 13, marginBottom: 14, lineHeight: 1.5 }}>{event.description}</div>
      )}

      {loading ? (
        <div style={{ color: '#94a3b8', textAlign: 'center', padding: 40 }}>Chargement…</div>
      ) : !isConcours ? (
        <div>
          {sessions.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 14, textAlign: 'center', padding: '40px 0' }}>Aucune occurrence.</div>
          ) : sessions.map(o => <OccurrenceCard key={o.id} occ={o} event={event} onClick={() => undefined} />)}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e2e8f0', marginBottom: 16, overflowX: 'auto' }}>
            <button style={tabStyle(tab === 'cand')} onClick={() => setTab('cand')}>
              👥 Candidatures ({pendingApps.length})
            </button>
            {sessions.length > 0
              ? sessions.map((o, i) => (
                  <button key={o.id} style={tabStyle(tab === o.id)} onClick={() => setTab(o.id)}>
                    Session {i + 1} · {formatDate(o.occurrence_date)}{o.participants_locked ? ' 🔒' : ''}
                  </button>
                ))
              : (launch?.enabled && (
                  <button style={tabStyle(tab === 'sess1')} onClick={() => setTab('sess1')}>
                    Session 1 ({selectedCount}/{minP})
                  </button>
                ))}
          </div>

          {tab === 'cand' && (
            <>
              <EventRecap event={event} sessionsCount={sessions.length} />
              {pendingApps.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 14, textAlign: 'center', padding: '40px 0' }}>
                Aucune candidature en attente.
              </div>
            ) : (
              <div>
                {pendingApps.map(a => candRow(a,
                  <>
                    <button onClick={() => handleSelect(a.id)} disabled={busy === a.id} style={btn('#16a34a')}>
                      {busy === a.id ? '…' : '✓ Sélectionner'}
                    </button>
                    {canManage && (
                      <button onClick={() => handleDeleteApp(a.id)} style={{ ...btnOutline('#dc2626'), padding: '6px 10px', fontSize: 12 }}>🗑</button>
                    )}
                  </>
                ))}
              </div>
            )}
            </>
          )}

          {tab === 'sess1' && conditionalPending && (
            <div>
              <EventRecap event={event} sessionsCount={sessions.length} />
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
                  Démarrage conditionné — {selectedCount}/{minP} participants sélectionnés
                </div>
                <div style={{ fontSize: 13, color: '#475569', marginBottom: 14 }}>
                  Sélectionne les candidats dans « Candidatures ». Tant que la session n'est pas lancée,
                  tu peux consulter leur formulaire et les remettre en candidature.
                </div>
                {selectedCount >= minP && minP > 0 ? (
                  canManage ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input type="date" value={launchDate} onChange={e => setLaunchDate(e.target.value)}
                        style={{ ...inputSt, width: 'auto' }} />
                      <button onClick={handleLaunch} disabled={launching || !launchDate} style={btn('#16a34a')}>
                        {launching ? 'Génération…' : '🚀 Générer les occurrences & lancer la Session 1'}
                      </button>
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px' }}>
                      Seuil atteint — en attente de validation du lancement par un responsable ou l'admin.
                    </div>
                  )
                ) : (
                  <div style={{ fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px' }}>
                    ⏳ Encore {Math.max(0, minP - selectedCount)} participant(s) à sélectionner.
                  </div>
                )}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                Sélectionnés ({selectedApps.length})
              </div>
              {selectedApps.length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: 14, textAlign: 'center', padding: '24px 0' }}>
                  Aucun candidat sélectionné.
                </div>
              ) : selectedApps.map(a => candRow(a,
                <>
                  <button onClick={() => handleUnselect(a.id)} disabled={busy === a.id} style={{ ...btnOutline('#d97706'), padding: '6px 10px', fontSize: 12 }}>↩ Remettre</button>
                  {canManage && (
                    <button onClick={() => handleDeleteApp(a.id)} style={{ ...btnOutline('#dc2626'), padding: '6px 10px', fontSize: 12 }}>🗑</button>
                  )}
                </>
              ))}
            </div>
          )}

          {typeof tab === 'number' && (() => {
            const idx = sessions.findIndex(o => o.id === tab);
            if (idx < 0) return null;
            const sess = sessions[idx];
            const list = partMap[tab];
            const next = sessions[idx + 1];
            const locked = !!sess.participants_locked;
            return (
              <div>
                <EventRecap event={event} sessionsCount={sessions.length} posterFilename={sess.image_filename || event.image_filename} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, color: '#475569', flex: 1 }}>
                    Session {idx + 1} — {formatDate(sess.occurrence_date)}
                    {sess.time_start && ` · ${formatTime(sess.time_start)}`}
                    {list ? ` · ${list.length} participant${list.length > 1 ? 's' : ''}` : ''}
                    {locked && <span style={{ ...badge('#dc2626'), marginLeft: 8 }}>🔒 Clôturée</span>}
                  </div>
                  {canManage && (
                    <button onClick={() => handleToggleLock(tab as number, locked)}
                      style={{ ...btnOutline(locked ? '#dc2626' : '#16a34a'), padding: '6px 12px', fontSize: 12 }}>
                      {locked ? '🔓 Décloturer' : '🔒 Clôturer la session'}
                    </button>
                  )}
                  {next && !locked && list && list.some(p => p.status === 'winner' || p.status === 'finalist') && (
                    <button onClick={() => handleAdvance(tab as number)} disabled={advancing} style={btn('#16a34a')}
                      title="Gagnants → session suivante · Finalistes → dernière session (finale)">
                      {advancing ? '…' : '⏭ Faire avancer (gagnants → suivante, finalistes → finale)'}
                    </button>
                  )}
                </div>
                {list === undefined ? (
                  <div style={{ color: '#94a3b8', textAlign: 'center', padding: 30 }}>Chargement…</div>
                ) : list.length === 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: 14, textAlign: 'center', padding: '40px 0' }}>
                    Aucun participant dans cette session. Sélectionne des candidats depuis « Candidatures ».
                  </div>
                ) : (
                  list.map(p => (
                    <ParticipantRow key={p.id} p={p} eventId={event.id} occId={tab as number}
                      locked={locked} canManage={canManage} isLast={idx === sessions.length - 1}
                      onUpdated={() => loadParts(tab as number)}
                      onRemove={() => handleRemovePart(tab as number, p.id)}
                      onDelete={() => handleDeleteApp(p.application_id)} />
                  ))
                )}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

// ─── MesEvenements ─────────────────────────────────────────────────────────
export default function MesEvenements({ streamerId, isMobile }: Props) {
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [occsMap, setOccsMap] = useState<Record<number, EventOccurrence[]>>({});
  const [selectedEvent, setSelectedEvent] = useState<LiveEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'live' | 'concours'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'upcoming' | 'past'>('all');
  const [myReqs, setMyReqs] = useState<EventRequest[]>([]);
  const [reqOpen, setReqOpen] = useState(false);
  const [rf, setRf] = useState({ title: '', description: '', event_type: 'concours', desired_schedule: '', desired_prizes: '' });
  const [reqBusy, setReqBusy] = useState(false);
  const [reqMsg, setReqMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getEvents();
      const mine = r.data.filter(e => e.streamers?.some(s => s.streamer_id === streamerId));
      setEvents(mine);
      const map: Record<number, EventOccurrence[]> = {};
      await Promise.all(mine.map(async e => {
        try { const or = await getEventOccurrences(e.id); map[e.id] = or.data; }
        catch { map[e.id] = []; }
      }));
      setOccsMap(map);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [streamerId]);

  const loadReqs = useCallback(async () => {
    try { const r = await getEventRequests(); setMyReqs(r.data.filter(x => x.status === 'pending')); }
    catch { setMyReqs([]); }
  }, []);

  useEffect(() => { load(); loadReqs(); }, [load, loadReqs]);

  const today = new Date().toISOString().slice(0, 10);
  const nextOcc = (eventId: number): EventOccurrence | null => {
    const list = occsMap[eventId] ?? [];
    return list
      .filter(o => o.occurrence_date >= today && o.status !== 'cancelled')
      .sort((a, b) => a.occurrence_date.localeCompare(b.occurrence_date))[0] ?? null;
  };
  const isUpcoming = (e: LiveEvent) => nextOcc(e.id) !== null || !!e.pending_start;

  const submitRequest = async () => {
    if (!rf.title.trim()) return;
    setReqBusy(true); setReqMsg('');
    try {
      await createEventRequest({
        title: rf.title.trim(),
        description: rf.description.trim() || null,
        event_type: rf.event_type,
        desired_schedule: rf.desired_schedule.trim() || null,
        desired_prizes: rf.desired_prizes.trim() || null,
      });
      setReqMsg('Demande envoyée ✓');
      setRf({ title: '', description: '', event_type: 'concours', desired_schedule: '', desired_prizes: '' });
      await loadReqs();
      setTimeout(() => { setReqOpen(false); setReqMsg(''); }, 1200);
    } catch { setReqMsg('Erreur lors de l\'envoi'); } finally { setReqBusy(false); }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Chargement…</div>;
  }

  const filtered = events
    .filter(e => typeFilter === 'all' || e.event_type === typeFilter)
    .filter(e => statusFilter === 'all' || (statusFilter === 'upcoming' ? isUpcoming(e) : !isUpcoming(e)))
    .filter(e => !search || e.title.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const na = nextOcc(a.id), nb = nextOcc(b.id);
      return (na?.occurrence_date ?? '9999').localeCompare(nb?.occurrence_date ?? '9999');
    });

  const renderEventCard = (event: LiveEvent) => {
    const next = nextOcc(event.id);
    const color = STATUS_COLOR[next?.status ?? 'completed'];
    const isSel = selectedEvent?.id === event.id;
    const accent = event.event_type === 'concours' ? '#7c3aed' : '#2563eb';
    return (
      <div key={event.id}
        style={{
          background: '#fff', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', marginBottom: 8,
          border: isSel ? `2px solid ${accent}` : '1px solid #e2e8f0',
          boxShadow: isSel ? `0 0 0 3px ${accent}22` : '0 1px 2px rgba(0,0,0,0.04)',
          display: 'flex', flexDirection: 'column', flexShrink: 0, transition: 'all 0.12s',
        }}
        onClick={() => setSelectedEvent(event)}>
        {event.image_filename ? (
          <img src={`/api/event-images/${event.image_filename}`} alt="" loading="lazy"
            style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block', background: '#f1f5f9' }} />
        ) : (
          <div style={{ width: '100%', height: 90, background: `${accent}1f`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 38 }}>
            {event.event_type === 'concours' ? '🏆' : '📡'}
          </div>
        )}
        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', lineHeight: 1.3, wordBreak: 'break-word' }}>{event.title}</div>
          {next ? (
            <div style={{ fontSize: 11, color: '#64748b' }}>
              📅 {formatDate(next.occurrence_date)}
              {next.time_start && ` · ${formatTime(next.time_start)}`}
            </div>
          ) : event.pending_start ? (
            <div style={{ fontSize: 11, color: '#64748b' }}>{event.recurrence_summary || 'En attente de participants'}</div>
          ) : (
            <div style={{ fontSize: 11, color: '#94a3b8' }}>Aucune date à venir</div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {event.event_type === 'concours' && <span style={badge('#7c3aed')}>Concours</span>}
            {next && <span style={badge(color)}>{STATUS_LABEL[next.status] ?? next.status}</span>}
          </div>
          {event.winners && event.winners.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12 }}>🏆</span>
              {event.winners.map(w => (
                <span key={w.application_id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Avatar url={w.avatar_url} name={w.name} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#0f172a' }}>{w.name}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const seg = (active: boolean): React.CSSProperties => ({
    flex: 1, borderRadius: 6, border: 'none', fontSize: 11, padding: '5px 0', cursor: 'pointer',
    fontWeight: active ? 700 : 400,
    background: active ? '#0f172a' : '#f1f5f9',
    color: active ? '#fff' : '#64748b',
  });

  const listPanel = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ color: '#0f172a', fontSize: isMobile ? 18 : 20, margin: 0, fontWeight: 700 }}>Mes Événements</h2>
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…"
        style={{ ...inputSt, marginBottom: 8, background: '#fff' }} />
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {([['all', 'Tous'], ['live', '📡 Lives'], ['concours', '🏆 Concours']] as const).map(([v, l]) => (
          <button key={v} style={seg(typeFilter === v)} onClick={() => setTypeFilter(v as any)}>{l}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
        {([['all', 'Tous'], ['upcoming', 'À venir'], ['past', 'Passé']] as const).map(([v, l]) => (
          <button key={v} style={seg(statusFilter === v)} onClick={() => setStatusFilter(v as any)}>{l}</button>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0', fontSize: 14 }}>
            Aucun événement.
          </div>
        ) : filtered.map(renderEventCard)}
      </div>
      {myReqs.length > 0 && (
        <div style={{ fontSize: 12, color: '#7c3aed', margin: '10px 0 6px', fontWeight: 600 }}>
          {myReqs.length} demande{myReqs.length > 1 ? 's' : ''} en attente
        </div>
      )}
      <button onClick={() => setReqOpen(true)}
        style={{ width: '100%', padding: '10px 0', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: myReqs.length ? 0 : 12 }}>
        ➕ Demander un événement
      </button>
    </div>
  );

  const requestModal = reqOpen && (
    <div onClick={() => !reqBusy && setReqOpen(false)}
      style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 14, padding: 22, width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>Demander un événement</div>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Titre *</label>
        <input value={rf.title} onChange={e => setRf(f => ({ ...f, title: e.target.value }))}
          style={{ ...inputSt, margin: '4px 0 12px' }} />
        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Description</label>
        <textarea value={rf.description} onChange={e => setRf(f => ({ ...f, description: e.target.value }))}
          rows={3} style={{ ...inputSt, margin: '4px 0 12px', resize: 'vertical' }} />
        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Type</label>
        <div style={{ display: 'flex', gap: 8, margin: '4px 0 12px' }}>
          {(['live', 'concours'] as const).map(t => (
            <button key={t} onClick={() => setRf(f => ({ ...f, event_type: t }))}
              style={{ flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                border: `1px solid ${rf.event_type === t ? '#7c3aed' : '#cbd5e1'}`,
                background: rf.event_type === t ? '#f5f3ff' : '#fff',
                color: rf.event_type === t ? '#7c3aed' : '#64748b', fontWeight: rf.event_type === t ? 700 : 400 }}>
              {t === 'live' ? '📡 Live' : '🏆 Concours'}
            </button>
          ))}
        </div>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Date / fréquence souhaitée</label>
        <input value={rf.desired_schedule} onChange={e => setRf(f => ({ ...f, desired_schedule: e.target.value }))}
          placeholder="Ex : 3 vendredis 21h-00h" style={{ ...inputSt, margin: '4px 0 12px' }} />
        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Lots à gagner souhaités (1 par ligne)</label>
        <textarea value={rf.desired_prizes} onChange={e => setRf(f => ({ ...f, desired_prizes: e.target.value }))}
          rows={3} style={{ ...inputSt, margin: '4px 0 14px', resize: 'vertical' }} />
        {reqMsg && <div style={{ fontSize: 13, marginBottom: 10, color: reqMsg.includes('Erreur') ? '#dc2626' : '#16a34a' }}>{reqMsg}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={() => setReqOpen(false)} disabled={reqBusy} style={btnOutline('#64748b')}>Annuler</button>
          <button onClick={submitRequest} disabled={reqBusy || !rf.title.trim()} style={btn('#7c3aed')}>
            {reqBusy ? '…' : 'Envoyer la demande'}
          </button>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ padding: '16px', background: '#f8fafc', minHeight: '100%' }}>
        {selectedEvent ? (
          <EventDetail event={selectedEvent} showBack onBack={() => { setSelectedEvent(null); load(); }} />
        ) : listPanel}
        {requestModal}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 20, padding: '20px', background: '#f8fafc', minHeight: '100%', height: '100%', alignItems: 'stretch', boxSizing: 'border-box' }}>
      <div style={{ width: 340, flexShrink: 0 }}>{listPanel}</div>
      <div style={{ flex: 1, minWidth: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, minHeight: 400, overflowY: 'auto', overflowX: 'hidden' }}>
        {selectedEvent ? (
          <EventDetail key={selectedEvent.id} event={selectedEvent}
            onBack={() => { setSelectedEvent(null); load(); }} />
        ) : (
          <div style={{ color: '#94a3b8', textAlign: 'center', padding: '80px 20px', fontSize: 14 }}>
            ← Sélectionne un événement pour voir les candidatures et les sessions.
          </div>
        )}
      </div>
      {requestModal}
    </div>
  );
}
